import { Component, ComponentInterface, Event, EventEmitter, h, Listen, Prop, State } from '@stencil/core';
import { UiHubPages, UiPage } from '../ui-cms-navigation';

@Component({
  tag: 'ui-cms-navigation-element',
  styleUrl: 'ui-cms-navigation-element.scss',
})
export class UiNavigationElement implements ComponentInterface {
  NavigationElement!: HTMLDivElement;

  MegaMenuElement!: HTMLUListElement;

  timeoutHandlerOn: any;

  timeoutHandlerOff: any;

  timeoutValue = 200;

  dataLevel = 1;

  @Prop() elementIndex: number;

  @Prop() page: UiPage = {} as any;

  // SET in ui-cms-header
  // allows default navigation on ui-cms-navigation-element menu item click if TRUE
  // prevents default navigation on ui-cms-navigation-element menu item click and emits triggerExternalNavigation event if FALSE
  @Prop() navigateOnClick: boolean;

  @Event() linkClick: EventEmitter<{ page: UiPage }>;

  @Event() triggerExternalNavigation: EventEmitter<{ page: UiPage }>;

  @State()
  currentMegaMenuItemIndex = 0;

  @State()
  megaMenuItems: HTMLElement[] = [];

  @State()
  isMegaMenuOpened: boolean = false;

  @Listen('keydown', { target: 'body' }) onKeyDown(e) {
    if (e.key === 'Escape' && !this.page.url) {
      e.preventDefault();
      this.closeMenu();
    }
  }

  getDeeperLevel(page: UiPage, dataLevel: number) {
    const { active, description, title, url } = page;
    const noUrl = !url || !url.length;
    let isActive: boolean = active;

    if (active && dataLevel === 2 && page?.pages) {
      isActive = !this.checkIsLowerLevelActive(page?.pages);
    }

    return (
      <li>
        {noUrl ? (
          <span class="sd-headline sd-headline--size-base not-selectable">{title}</span>
        ) : (
          <sd-navigation-item
            class={{ 'cursor-default': page.pages?.length, 'menu-item-level-3': dataLevel === 3 && !isActive }}
            data-level={dataLevel}
            size={dataLevel === 2 ? 'base' : 'sm'}
            vertical
            href={url}
            current={isActive}
            onClick={(e) => this.onLinkClick(page, e)}
          >
            {isActive || dataLevel === 2 ? <b class="cursor-pointer">{title}</b> : title}
            {description && dataLevel === 2 && <span slot="description">{description}</span>}
          </sd-navigation-item>
        )}
        {page?.pages && <ul>{page.pages?.map((singlePage) => this.getDeeperLevel(singlePage, dataLevel + 1))}</ul>}
      </li>
    );
  }

  checkIsLowerLevelActive(pages = []) {
    const activePages = pages.filter((page) => page.active === true);

    return activePages.length > 0;
  }

  getHubNavigation(hubPage: UiHubPages) {
    return (
      <li class="hub-navigation">
        <span class="hub-navigation__headline sd-headline sd-headline--size-lg">{hubPage.title}</span>
        <ul>{hubPage.pages?.map((page) => this.getHubItem(page))}</ul>
      </li>
    );
  }

  getHubItem(page: UiPage) {
    const { title, description, url } = page;

    return (
      <li>
        <sd-navigation-item target="_blank" size="base" vertical href={url} onClick={(e) => this.onLinkClick(page, e)}>
          <b>{title}</b>
          {description && <span slot="description">{description}</span>}
        </sd-navigation-item>
      </li>
    );
  }

  private get megaMenuPosition() {
    const mainNav = document.querySelector('ui-cms-navigation');

    return { left: `${mainNav.getBoundingClientRect().left}px`, top: `${mainNav.getBoundingClientRect().bottom}px` };
  }

  private onLinkClick(page: UiPage, event?: Event) {
    event?.stopPropagation();

    this.linkClick.emit({ page });
  }

  private onMenuItemClick(e) {
    e.preventDefault();
    e.stopPropagation();

    if (this.navigateOnClick) {
      if (this.NavigationElement.getAttribute('href') !== null) {
        // eslint-disable-next-line no-restricted-globals
        location.href = this.NavigationElement.getAttribute('href');
        this.onLinkClick(this.page, e);
      }
    } else {
      this.triggerExternalNavigation.emit({ page: this.page });
    }
  }

  private closeMenu() {
    this.currentMegaMenuItemIndex = 0;
    this.isMegaMenuOpened = false;
    this.NavigationElement.shadowRoot.querySelector('button').setAttribute('aria-expanded', 'false');
  }

  private openMenu() {
    this.isMegaMenuOpened = true;
    this.NavigationElement.shadowRoot.querySelector('button').setAttribute('aria-expanded', 'true');
  }

  private onMouseEnter() {
    if (this.MegaMenuElement) {
      window.clearTimeout(this.timeoutHandlerOn);

      if (this.isMegaMenuOpened) {
        this.timeoutValue = 160;
      }

      this.timeoutHandlerOn = window.setTimeout(() => {
        this.openMenu();
      }, this.timeoutValue);
    }
  }

  private onMouseLeave() {
    if (this.MegaMenuElement && !this.MegaMenuElement.matches(':hover')) {
      window.clearTimeout(this.timeoutHandlerOn);

      this.timeoutHandlerOff = window.setTimeout(() => {
        this.closeMenu();
      }, 250);
    }
  }

  private onKeyDownEvent(e, containsUrl: boolean) {
    const { key, target } = e;

    if (target.id.includes('main-nav-item-')) {
      if (key === 'Enter') {
        e.preventDefault();

        if (containsUrl) {
          this.onMenuItemClick(e);
        } else {
          this.openMenu();
        }
      }

      if (key === 'ArrowDown' && !containsUrl) {
        e.preventDefault();
        this.currentMegaMenuItemIndex = 0;
        this.openMenu();

        requestAnimationFrame(() => {
          this.megaMenuItems[0].focus();
        });
      }

      if (key === 'ArrowUp') {
        this.closeMenu();
      }
    }
  }

  private navigateWithKeyboard(e) {
    if (e.key === 'ArrowDown' || (!e.shiftKey && e.key === 'Tab')) {
      e.preventDefault();
      if (this.currentMegaMenuItemIndex === this.megaMenuItems.length - 1) {
        const nextMainMenuItem = document
          .getElementById(`main-nav-item-${this.elementIndex + 1}`)
          .shadowRoot.querySelectorAll('button, a')[0] as HTMLElement;

        nextMainMenuItem.focus();
        this.currentMegaMenuItemIndex = 0;
        this.closeMenu();
      } else {
        this.currentMegaMenuItemIndex += 1;
        this.megaMenuItems[this.currentMegaMenuItemIndex].focus();
      }
    }
    if (e.key === 'ArrowUp' || (e.shiftKey && e.key === 'Tab')) {
      e.preventDefault();
      if (this.currentMegaMenuItemIndex === 0) {
        this.closeMenu();
      } else {
        this.currentMegaMenuItemIndex -= 1;
        this.megaMenuItems[this.currentMegaMenuItemIndex].focus();
      }
    }
    if (e.key === 'Enter') {
      this.closeMenu();
    }
  }

  componentDidLoad() {
    setTimeout(() => {
      const shadowRootElement = this.NavigationElement.shadowRoot?.querySelector('button');

      shadowRootElement?.setAttribute('aria-haspopup', 'menu');
      shadowRootElement?.setAttribute('aria-expanded', 'false');

      if (this.MegaMenuElement) {
        const sdNavigationItems = Array.from(this.MegaMenuElement?.querySelectorAll('sd-navigation-item'));
        this.megaMenuItems = sdNavigationItems.map((item) => item.shadowRoot?.querySelector('button, a'));
      }
    }, 0);
  }

  render() {
    const itemsInNavigation = this.page?.pages ? this.page?.pages.length : 0;
    const rows = Math.ceil(itemsInNavigation / 4);

    return (
      <sd-navigation-item
        ref={(el) => {
          this.NavigationElement = el as HTMLDivElement;
        }}
        id={`main-nav-item-${this.elementIndex}`}
        class={{
          'cursor-default': !this.page.url,
          [`main-menu-item static first-level columns${itemsInNavigation}`]: true,
        }}
        aria-expanded={this.isMegaMenuOpened.toString()}
        data-level={this.dataLevel}
        size="base"
        href={this.page.url}
        current={this.page.active}
        onClick={(e) => this.onMenuItemClick(e)}
        onMouseEnter={() => this.onMouseEnter()}
        onMouseLeave={() => this.onMouseLeave()}
        onKeyDown={(e) => this.onKeyDownEvent(e, !!this.page.url)}
      >
        <b>{this.page.title}</b>
        {this.page?.pages && (
          <ul
            ref={(el) => {
              this.MegaMenuElement = el as HTMLUListElement;
            }}
            id={`mega-menu-${this.elementIndex}`}
            role="menu"
            aria-hidden={(!this.isMegaMenuOpened).toString()}
            class={{
              [`mega-menu rows-${rows}`]: true,
              'mega-menu--opened': this.isMegaMenuOpened,
              'mega-menu--closed': !this.isMegaMenuOpened,
              multilines: rows > 1,
            }}
            style={this.megaMenuPosition}
            data-columns="4"
            data-rows={rows}
            onKeyDown={(e) => {
              this.navigateWithKeyboard(e);
            }}
          >
            {this.page?.pages.map((page) => this.getDeeperLevel(page, this.dataLevel + 1))}
            {this.page?.hubPages?.pages?.length > 0 && this.getHubNavigation(this.page.hubPages)}
          </ul>
        )}
      </sd-navigation-item>
    );
  }
}
